/**
 * Settings API Schemas
 *
 * Schemas for prompt profiles, provider credentials, and delivery config.
 *
 * Endpoints:
 * - POST /api/v1/settings/preview-analyze - Test prompt profile with sample data
 * - GET /api/v1/organizations/:id/provider-credentials - List provider credentials
 * - POST /api/v1/organizations/:id/provider-credentials - Create credentials
 * - PATCH /api/v1/organizations/:id/provider-credentials/:credId - Update credentials
 * - DELETE /api/v1/organizations/:id/provider-credentials/:credId - Delete credentials
 * The delivery-config response is `orgDeliveryConfigResponseSchema` (organizations.ts),
 * the field-provisioning responses are in bitrix24.ts.
 */

import { z } from 'zod/v4';

// ============================================================================
// Preview Analyze Schemas (prompt profiles now in ./prompt-profiles.ts)
// ============================================================================

/**
 * Response for prompt profile preview - matches POST /api/v1/settings/preview-analyze
 *
 * Used to test prompt profiles with sample transcript data before applying to projects.
 */
export const previewAnalyzeResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    /** Structured output from LLM (e.g., { qualityScore: 85, sentiment: "positive" }) */
    structuredOutput: z.record(z.string(), z.unknown()),
    /**
     * The prompts the server actually sent.
     *
     * The route has always returned them; this contract dropped them, and Zod
     * strips what it does not declare — so the panel could only show a prompt
     * the FRONTEND assembled from the profile and toggles. That was tolerable
     * while everything was org-level, and stopped being tolerable when the
     * preview gained project scope (#321): the project's forbidden words and
     * report language exist only on the server side of this call, so the
     * locally built prompt is not the one that was analysed.
     *
     * Optional: an older Worker deployed before this field is still a valid
     * response, and the panel falls back to its local build.
     */
    prompts: z
      .object({
        systemPrompt: z.string(),
        userPrompt: z.string(),
      })
      .optional(),
  }),
  /** Processing metadata (model, tokens, latency) */
  metadata: z.record(z.string(), z.unknown()),
});

// ============================================================================
// Provider Credentials Schemas
// ============================================================================

/**
 * Provider types for credential management
 *
 * - `asr`: Automatic Speech Recognition (ElevenLabs)
 * - `llm`: Large Language Models (OpenAI, Anthropic)
 * - `email`: Email providers (Postmark, SendGrid)
 * - `crm`: CRM integrations (Bitrix24, Salesforce)
 */
export const providerTypeSchema = z.enum(['asr', 'llm', 'email', 'crm']);

/**
 * Provider credential entity as returned by the API.
 *
 * SECURITY: the secret payload (`credentialsJson` — API keys / client secrets)
 * is write-only. It is accepted on create/update but NEVER serialized back to
 * the client. "Configured" is inferred from row presence; to change a key the
 * owner submits a fresh value. See audit P0-2.
 */
export const providerCredentialSchema = z.object({
  id: z.string(),
  providerType: providerTypeSchema,
  /** Provider name (e.g., 'elevenlabs', 'openai', 'postmark', 'bitrix24') */
  providerName: z.string(),
  /** Whether this credential is active for use */
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * Response for listing provider credentials - matches GET /api/v1/organizations/:id/provider-credentials
 */
export const providerCredentialsListResponseSchema = z.object({
  credentials: z.array(providerCredentialSchema),
});

/**
 * Request for creating provider credential - POST /api/v1/organizations/:id/provider-credentials
 */
export const createProviderCredentialRequestSchema = z
  .object({
    providerType: providerTypeSchema,
    providerName: z.string().min(1).max(100),
    /** JSON string with provider-specific credentials (will be encrypted) */
    credentialsJson: z.string().min(1),
    isActive: z.boolean().default(true),
  })
  .strict();

/**
 * Request for updating provider credential - PATCH /api/v1/organizations/:id/provider-credentials/:credId
 */
export const updateProviderCredentialRequestSchema = z
  .object({
    providerName: z.string().min(1).max(100).optional(),
    /** Updated credentials JSON (will be re-encrypted) */
    credentialsJson: z.string().min(1).optional(),
    isActive: z.boolean().optional(),
  })
  .strict();

// ============================================================================
// Delivery Config Schemas
// ============================================================================

/**
 * Configuration for which analysis fields to deliver to CRM
 */
export const deliveryFieldsSchema = z.object({
  /** Include full transcript */
  transcript: z.boolean(),
  /** Include AI-generated summary */
  summary: z.boolean(),
  /** Include quality score (0-100) */
  qualityScore: z.boolean(),
  /** Include sale probability (0-100) */
  saleProbability: z.boolean(),
  /** Include extracted keywords */
  keywords: z.boolean(),
  /** Include sentiment analysis */
  sentiment: z.boolean(),
});

/**
 * Raw delivery config shape (accepts both new boolean fields and old destination enum)
 */
const deliveryConfigRawSchema = z.object({
  deliverToCrmCard: z.boolean().default(false),
  deliverToSmartProcess: z.boolean().default(false),
  destination: z.enum(['crm_card', 'smart_process']).optional(),
  smartProcessId: z.string().optional(),
  fields: deliveryFieldsSchema,
});

/**
 * Delivery configuration for a project
 *
 * Normalizes old `destination` enum format to dual booleans via transform.
 */
export const deliveryConfigSchema = deliveryConfigRawSchema.transform((data) => ({
  deliverToCrmCard: data.deliverToCrmCard || data.destination === 'crm_card',
  deliverToSmartProcess:
    data.deliverToSmartProcess || data.destination === 'smart_process',
  smartProcessId: data.smartProcessId,
  fields: data.fields,
}));

/**
 * Bitrix24 Smart Process metadata
 */
export const smartProcessSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** Bitrix24 entity type ID */
  entityTypeId: z.number(),
});

/**
 * Status of CRM field creation
 */
export const fieldStatusSchema = z.object({
  /** Internal field name (e.g., 'qualityScore') */
  name: z.string(),
  /** Display label in CRM */
  label: z.string(),
  /** Whether field has been created in CRM */
  created: z.boolean(),
  /** CRM field ID if created */
  fieldId: z.string().optional(),
});

/**
 * Delivery history entry for audit trail
 */
export const deliveryHistoryItemSchema = z.object({
  id: z.string(),
  recordId: z.string(),
  /** Delivery status */
  status: z.enum(['success', 'failed', 'pending']),
  timestamp: z.string(),
  /** Error message if delivery failed */
  error: z.string().optional(),
});

/**
 * Response for testing delivery - matches POST /api/v1/organizations/:orgId/delivery/test
 */
export const deliveryTestResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

// ============================================================================
// Type exports
// ============================================================================

export type PreviewAnalyzeResponse = z.infer<typeof previewAnalyzeResponseSchema>;
export type ProviderType = z.infer<typeof providerTypeSchema>;
export type ProviderCredential = z.infer<typeof providerCredentialSchema>;
export type ProviderCredentialsListResponse = z.infer<
  typeof providerCredentialsListResponseSchema
>;
export type CreateProviderCredentialRequest = z.infer<
  typeof createProviderCredentialRequestSchema
>;
export type UpdateProviderCredentialRequest = z.infer<
  typeof updateProviderCredentialRequestSchema
>;
export type DeliveryFields = z.infer<typeof deliveryFieldsSchema>;
export type DeliveryConfig = z.infer<typeof deliveryConfigSchema>;
export type SmartProcess = z.infer<typeof smartProcessSchema>;
export type FieldStatus = z.infer<typeof fieldStatusSchema>;
export type DeliveryHistoryItem = z.infer<typeof deliveryHistoryItemSchema>;
export type DeliveryTestResponse = z.infer<typeof deliveryTestResponseSchema>;

// ============================================================================
// Unlisted Source Request (offer layer, Phase 5)
// ============================================================================

/**
 * "My system isn't listed" — what a prospect tells us about their stack so we
 * can scope a connector. Every field is free text: the whole point is that
 * their system is one we do not have a name for yet.
 */
export const unlistedSourceRequestSchema = z.object({
  /** CRM in use, or an empty string when they have none. */
  crm: z.string().trim().max(200),
  /** Telephony / call platform. */
  telephony: z.string().trim().max(200),
  /** Where call recordings currently live (CRM, provider portal, S3, …). */
  recordingsLocation: z.string().trim().max(200),
  notes: z.string().trim().max(2000).optional(),
});

export type UnlistedSourceRequest = z.infer<typeof unlistedSourceRequestSchema>;

// ============================================================================
// Organization first day of the week (#864)
// ============================================================================

/**
 * `orgs.settings_json.weekStartsOn` — the organization's first day of the
 * week, an ISO weekday: 1 = Monday … 7 = Sunday. Written by
 * `PATCH /api/v1/settings` (`organization.weekStartsOn`, the
 * `organization.update` gate), answered by `GET /api/v1/settings` with the
 * resolved value — Monday when the organization never chose.
 *
 * Every member reads it — not from `GET /api/v1/settings`, which is
 * client/admin only, but from the payloads each of them already loads for the
 * organization context: the `/users/me` roster (`meResponseSchema`) and the
 * embedded session (`embeddedSessionResponseSchema`).
 *
 * It decides where a week begins for every member: the dashboard's Week
 * period, its navigation and label, the weekly series on My Progress and the
 * weekly digest's send day and window. The date math is
 * `packages/core/src/week-start.ts`.
 */
export const weekStartsOnSchema = z.number().int().min(1).max(7);

export type WeekStartsOnSetting = z.infer<typeof weekStartsOnSchema>;

const deletedCountSchema = z.number().int().nonnegative();

/**
 * What a retention change would delete on the next cleanup run (#2772) — the
 * answer of `GET /settings/retention-impact` (one organization) and
 * `GET /admin/platform-settings/retention/impact` (every organization that
 * inherits the platform window).
 *
 * Five classes, because the retention period deletes five kinds of data on
 * its own: records (with their audio, transcripts, analyses and everything
 * that follows the record), supervisors' coaching notes (#2629), coaching
 * meetings (#2775), AI assistant questions that are not about a record
 * (#1091) and deal states (#766). The last four are not tied to a record's
 * age, so a window that deletes no record can still delete them — the
 * confirmation dialog shows all five.
 * `cutoffDate: null` — unlimited retention, every count is 0.
 */
export const retentionDeletionsSchema = z.object({
  affectedRecords: deletedCountSchema,
  affectedCoachingNotes: deletedCountSchema,
  affectedCoachingSessions: deletedCountSchema,
  affectedAssistantTurns: deletedCountSchema,
  affectedDealStates: deletedCountSchema,
});

export const retentionImpactSchema = retentionDeletionsSchema.extend({
  days: z.number().int().nonnegative(),
  cutoffDate: z.string().nullable(),
  /**
   * Platform preview only: organizations with their own window, which a
   * platform change does not touch and the counts leave out.
   */
  organizationsWithOwnRetention: deletedCountSchema.optional(),
});

export const retentionImpactResponseSchema = z.object({ data: retentionImpactSchema });

/**
 * The five counts alone. Every consumer keys its list of classes on this type
 * (`Record<keyof RetentionDeletions, …>`), so a class added here fails to
 * compile until the server counts it and the dialogs name it.
 */
export type RetentionDeletions = z.infer<typeof retentionDeletionsSchema>;
export type RetentionImpact = z.infer<typeof retentionImpactSchema>;
export type RetentionImpactResponse = z.infer<typeof retentionImpactResponseSchema>;
